/**
 * GlobalSearchBar Component (T152)
 * Autocomplete dropdown search bar for the header
 * Searches across customers, appointments, invoices
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Calendar, FileText, X, Loader2 } from 'lucide-react';
import { useQuickSearch } from '../../hooks/useSearch';
import HighlightText from './HighlightText';

export default function GlobalSearchBar() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: results, isLoading } = useQuickSearch(query);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const hasResults = results && results.totalResults > 0;
  const showDropdown = isOpen && query.length >= 2;

  const handleNavigate = (path: string) => {
    setIsOpen(false);
    setQuery('');
    navigate(path);
  };

  const handleViewAll = () => {
    handleNavigate(`/search?q=${encodeURIComponent(query)}`);
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search... (⌘K)"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="w-full pl-10 pr-10 py-2 text-sm bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:bg-background transition-colors"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setIsOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {showDropdown && (
        <div className="absolute top-full mt-2 w-full bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden max-h-[400px] overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && !hasResults && query.length >= 2 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No results found for "{query}"
            </div>
          )}

          {!isLoading && hasResults && (
            <>
              {/* Customers */}
              {results.customers.count > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/30">
                    Customers ({results.customers.count})
                  </div>
                  {results.customers.results.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => handleNavigate(`/customers/${c.id}`)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 text-left transition-colors"
                    >
                      <User className="w-4 h-4 text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          <HighlightText text={c.name} query={query} />
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                      </div>
                      <span className="text-xs text-muted-foreground capitalize">
                        {c.matchField}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Appointments */}
              {results.appointments.count > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/30">
                    Appointments ({results.appointments.count})
                  </div>
                  {results.appointments.results.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => handleNavigate('/appointments')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 text-left transition-colors"
                    >
                      <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          <HighlightText text={a.title} query={query} />
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {a.customerName} · {new Date(a.startTime).toLocaleDateString()}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Invoices */}
              {results.invoices.count > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-muted/30">
                    Invoices ({results.invoices.count})
                  </div>
                  {results.invoices.results.map((inv) => (
                    <button
                      key={inv.id}
                      onClick={() => handleNavigate('/invoices')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 text-left transition-colors"
                    >
                      <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          <HighlightText text={inv.invoiceNumber} query={query} />
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {inv.customerName} · ${inv.total.toFixed(2)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* View all results */}
              <button
                onClick={handleViewAll}
                className="w-full px-3 py-2.5 text-sm text-primary hover:bg-muted/50 text-center font-medium border-t border-border transition-colors"
              >
                View all {results.totalResults} results →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
